<template>
  <div>
    <label v-show="isReportMetadataPending">
      Export will become available when latest request processes.
    </label>

    <FlatActionButton
      :action="confirmExport"
      buttonClass="btnDefault"
      :disabled="disabled || isReportMetadataPending"
      :loading="isLoading || isReportMetadataPending"
      text="run report"
    />
  </div>
</template>

<script>
import { ItemTypeConsts, StoreModuleMap } from 'client/util/Constants';

import { mapActions, mapGetters } from 'vuex';
import { notificationModuleName as notificationModule } from '@jumpcloud/ui-notifications';
import DownloadUtil from 'client/util/DownloadUtil';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import FlatActionButton from 'client/components/Buttons/FlatActionButton.vue';
import JobUtils from 'client/util/JobUtils';
import ModalEventBus from 'components/Modal/ModalEventBus';
import ReportMetadata, { ReportTypes } from 'client/Reports/ReportMetadata';
import axios, { CancelToken } from 'axios';
import moment from 'moment';

const { reports } = ItemTypeConsts;

const reportsCollection = StoreModuleMap[reports];

export default {
  name: 'ReportExportActionButton',
  components: {
    FlatActionButton,
  },

  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    filters: {
      type: Array,
      default() {
        return [];
      },
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    reportType: {
      type: String,
      default: ReportTypes.userToDevice,
      validator: value => typeof ReportTypes[value] === 'string',
    },
  },

  data: () => ({
    requestSource: null,
  }),

  computed: {
    ...mapGetters(reportsCollection, ['isReportMetadataPending']),

    reportName() {
      return ReportMetadata[this.reportType].name;
    },
  },

  methods: {
    ...mapActions(reportsCollection, [
      'setReportPending',
    ]),

    ...mapActions(notificationModule, [
      'triggerErrorNotification',
      'triggerSuccessNotification',
    ]),

    cancelReport() {
      if (this.requestSource) {
        this.requestSource.cancel('Operation canceled by the user.');
      }
      ModalEventBus.close();
    },

    confirmExport() {
      const componentProps = {
        bodyText: 'Reports may take a few moments to complete. Once the requested report completes, it will be available for download (CSV/JSON) from the Stored Reports Queue at the top of this page or from the Stored Reports Page.',
        continueText: 'continue',
        handleCancel: this.cancelReport,
        handleContinue: async () => {
          componentProps.loading = true;
          ModalEventBus.close();
          await this.getReport();
        },
        headerText: 'Run Report',
        loading: false,
      };
      ModalEventBus.open({
        component: 'ConfirmationModal',
        componentProps,
      });
    },

    downloadReport({ data: reportJson }, reportType, reportName) {
      const today = new Date();
      const exportDate = moment(today).format('YYYYMMDD');

      // Convert returned JSON to CSV for download
      let reportCsv = JobUtils.convertJsonToCsv(reportJson, {
        fields: ReportMetadata[reportType].columns,
        sanitize: true,
      });

      // Append name of report and branding at top of CSV
      reportCsv = `${reportName} powered by JumpCloud\r\n${reportCsv}`;
      const reportNameInSnakeCase = reportName.split(' ').join('_').toLowerCase();
      const filename = `jc_${reportNameInSnakeCase}_${exportDate}.csv`;
      DownloadUtil.downloadFile(reportCsv, filename);

      this.triggerSuccessNotification({
        message: 'Report successfully downloaded!',
      });
    },

    async fetchReport() {
      try {
        await ReportMetadata[this.reportType].fetchReport({ filters: this.filters }, {
          cancelToken: this.requestSource.token,
        });
      } catch (err) {
        if (axios.isCancel(err)) {
          this.triggerErrorNotification({
            message: 'Report export cancelled.',
          });
        } else {
          this.triggerErrorNotification({
            message: `Error generating report. ${err.response.data.message}.`,
          });
        }
      } finally {
        this.setReportPending(false);
      }
    },

    getReport() {
      this.setReportPending(true);

      this.requestSource = CancelToken.source();

      EventHelper.triggerReportExported({
        reportName: this.reportType,
      });

      this.fetchReport();
    },
  },
};
</script>
